import {
  fetchAndActivate,
  getRemoteConfig,
  getValue,
  isSupported,
} from 'firebase/remote-config';
import { navigate } from 'gatsby';
import { initSDK } from './_firebase';
import { PATH_MAINTENANCE } from './constants/paths';
import { isPublicStandalonePath } from './routePolicy';

const MAINTENANCE_REMOTE_CONFIG_KEY = 'jsp_maintenance';
const MAINTENANCE_MESSAGE_KEY = 'MaintenanceMsg';

const DEFAULT_MAINTENANCE_CONFIG = {
  underConstruction: false,
  underConstructionPeriodStart: '',
  underConstructionPeriodEnd: '',
  underConstructionDescription: '',
};

const MOCK_MAINTENANCE_CONFIG = {
  code: '200',
  reason: 'success',
  data: {
    underConstruction: true,
    underConstructionDescription:
      '05/18(一) 23:00 ~ 05/19(二) 2:30 進行系統維護，完成後會立即開站， 造成不便，敬請見諒，謝謝您。',
    underConstructionPeriod: '2026/05/18 23:00 ~ 2026/05/19 2:30',
    underConstructionPeriodStart: '2026/05/18 23:00',
    underConstructionPeriodEnd: '2026/05/21 02:30',
    affiliateCookieLifetime: 86400,
  },
};

let remoteConfigInstance;

const shouldDebugMaintenance = process.env.GATSBY_USE_DEBUG === 'true';
const shouldUseMockMaintenance =
  process.env.NODE_ENV === 'development' &&
  process.env.GATSBY_MOCK_MAINTENANCE === 'true';

const debugMaintenance = (...messages) => {
  if (!shouldDebugMaintenance) return;

  console.log('[maintenance]', ...messages);
};

const getMaintenanceRemoteConfig = async () => {
  if (typeof window === 'undefined') {
    debugMaintenance('略過 SSR 環境');
    return null;
  }

  const supported = await isSupported();
  if (!supported) {
    debugMaintenance('Firebase Remote Config 不支援目前瀏覽器環境');
    return null;
  }

  if (remoteConfigInstance) return remoteConfigInstance;

  const app = initSDK();
  if (!app) {
    debugMaintenance('Firebase App 尚未初始化');
    return null;
  }

  remoteConfigInstance = getRemoteConfig(app);
  remoteConfigInstance.settings = {
    fetchTimeoutMillis: 10000,
    minimumFetchIntervalMillis: 60000,
  };
  remoteConfigInstance.defaultConfig = {
    [MAINTENANCE_REMOTE_CONFIG_KEY]: JSON.stringify(
      DEFAULT_MAINTENANCE_CONFIG
    ),
  };
  debugMaintenance('Firebase projectId:', app.options.projectId);

  return remoteConfigInstance;
};

const parseMaintenanceConfig = (rawValue) => {
  if (!rawValue) return DEFAULT_MAINTENANCE_CONFIG;

  try {
    const parsedValue =
      typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
    const maintenanceConfig = parsedValue?.data || parsedValue;

    return {
      ...DEFAULT_MAINTENANCE_CONFIG,
      ...maintenanceConfig,
    };
  } catch (error) {
    console.error('[maintenance] jsp_maintenance JSON 解析失敗', error);
    return DEFAULT_MAINTENANCE_CONFIG;
  }
};

const parseDate = (dateText) => {
  if (!dateText || typeof dateText !== 'string') return NaN;

  const matchedDate = dateText
    .trim()
    .match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})\s+(\d{1,2}):(\d{1,2})$/);

  if (!matchedDate) return NaN;

  const [, year, month, day, hour, minute] = matchedDate;
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute)
  ).getTime();
};

const isValidDateRange = (startDate, endDate) => {
  return !Number.isNaN(startDate) && !Number.isNaN(endDate);
};

export const getMaintenanceState = async () => {
  try {
    const configValue = shouldUseMockMaintenance
      ? JSON.stringify(MOCK_MAINTENANCE_CONFIG)
      : await getRemoteMaintenanceConfigValue();
    const maintenanceConfig = parseMaintenanceConfig(configValue);
    const constructionStart = parseDate(
      maintenanceConfig.underConstructionPeriodStart
    );
    const constructionEnd = parseDate(
      maintenanceConfig.underConstructionPeriodEnd
    );
    const nowDate = Date.now();
    const isOnPeriod =
      isValidDateRange(constructionStart, constructionEnd) &&
      nowDate > constructionStart &&
      nowDate < constructionEnd;
    const isUnderMaintenance =
      maintenanceConfig.underConstruction === true && isOnPeriod;

    debugMaintenance(
      shouldUseMockMaintenance ? 'Mock 原始值:' : 'Remote Config 原始值:',
      configValue
    );
    debugMaintenance('維護設定:', maintenanceConfig);
    debugMaintenance('維護判斷:', {
      constructionStart,
      constructionEnd,
      nowDate,
      isOnPeriod,
      isUnderMaintenance,
    });

    return {
      isUnderMaintenance,
      message: maintenanceConfig.underConstructionDescription || '',
    };
  } catch (error) {
    console.error('[maintenance] Firebase Remote Config 讀取失敗', error);
    return {
      isUnderMaintenance: false,
      message: '',
    };
  }
};

const getRemoteMaintenanceConfigValue = async () => {
  const remoteConfig = await getMaintenanceRemoteConfig();
  if (!remoteConfig) return '';

  await fetchAndActivate(remoteConfig);

  return getValue(remoteConfig, MAINTENANCE_REMOTE_CONFIG_KEY).asString();
};

export const redirectToMaintenanceIfNeeded = async () => {
  if (
    typeof window !== 'undefined' &&
    isPublicStandalonePath(window.location.pathname)
  ) {
    debugMaintenance('公開獨立路由略過維護導向:', window.location.pathname);
    return false;
  }

  const maintenanceState = await getMaintenanceState();

  if (!maintenanceState.isUnderMaintenance) return false;

  sessionStorage.setItem(MAINTENANCE_MESSAGE_KEY, maintenanceState.message);

  setTimeout(() => {
    navigate(PATH_MAINTENANCE, { replace: false });
  }, 400);

  return true;
};

export const getStoredMaintenanceMessage = () => {
  if (typeof window === 'undefined') return '';

  return sessionStorage.getItem(MAINTENANCE_MESSAGE_KEY) || '';
};

export const clearStoredMaintenanceMessage = () => {
  if (typeof window === 'undefined') return;

  sessionStorage.removeItem(MAINTENANCE_MESSAGE_KEY);
};
